import {
  Autocomplete,
  AutocompleteRenderInputParams,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useDebounce } from "hooks/debounceHooks";
import { useGeopuntLocation } from "hooks/locationHooks";
import { SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeopuntLocation } from "types/types";
import * as Yup from "yup";

interface FormValues {
  selectedLocation: GeopuntLocation | null;
}

interface Props {
  onSelectLocation: (location: GeopuntLocation) => void;
}

export function LayerSetDialogSearch(props: Props) {
  const { onSelectLocation } = props;
  const { t } = useTranslation();
  const initialValues: FormValues = {
    selectedLocation: null,
  };

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500).trim();

  const { data: locations, isLoading } = useGeopuntLocation(
    debouncedQuery,
    {
      enabled: debouncedQuery.length > 2,
    },
    1
  );

  const locationOptions = locations || [];

  function getLocationOptions(selectedLocation: GeopuntLocation | null) {
    if (
      selectedLocation &&
      !locationOptions.find(
        (location) =>
          location.FormattedAddress === selectedLocation.FormattedAddress
      )
    ) {
      return [...locationOptions, selectedLocation];
    }
    return locationOptions;
  }

  const validation = Yup.object().shape({
    selectedLocation: Yup.object().nullable().required(),
  });

  function onChange(
    event: SyntheticEvent,
    value: GeopuntLocation,
    reason: string,
    details?: string
  ) {
    if (reason === "selectOption") {
      onSubmit({ selectedLocation: value });
    }
  }

  function onSubmit(values: FormValues) {
    const { selectedLocation } = values;
    if (selectedLocation) {
      onSelectLocation(selectedLocation);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validation}
    >
      {(formikProps) => (
        <StyledForm>
          <Typography>Gemeente</Typography>
          <Field
            name="selectedLocation"
            component={Autocomplete}
            options={getLocationOptions(formikProps.values.selectedLocation)}
            filterOptions={(options: GeopuntLocation[], params: any) => {
              const arrayOfSplitString: string[] = params.inputValue
                .trim()
                .split(/\W+/);
              const filtered = options.filter((option) => {
                return arrayOfSplitString.some((word) => {
                  return option.FormattedAddress.toLowerCase().includes(
                    word.toLocaleLowerCase()
                  );
                });
              });
              return filtered;
            }}
            onChange={onChange}
            loading={isLoading}
            getOptionLabel={(location: GeopuntLocation) => {
              return location.FormattedAddress || "";
            }}
            isOptionEqualToValue={(
              option: GeopuntLocation,
              value: GeopuntLocation
            ) => {
              if (option.FormattedAddress === value.FormattedAddress) {
                return true;
              }
              return false;
            }}
            noOptionsText={t("label.noResultsFound")}
            onInputChange={(
              event: SyntheticEvent<Element, Event>,
              newInputValue: string
            ) => {
              setQuery(newInputValue);
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <StyledField
                {...params}
                variant="outlined"
                placeholder={t("label.searchAMunicipality")}
              />
            )}
          />
        </StyledForm>
      )}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  height: 100%;
  display: flex;
  background-color: ${({ theme }) => theme.palette.common.white};
  flex-direction: column;

  .MuiInputBase-root {
    height: 100%;
    padding: 0;
  }

  .MuiAutocomplete-root {
    width: 100%;
  }

  .MuiButton-root {
    align-self: stretch;
  }
  flex-grow: 1;

  .align-right {
    margin-left: auto;
    flex-shrink: 0;
    flex-wrap: nowrap;
  }

  .load-more-button {
    justify-content: center;
  }
`;

const StyledField = styled(TextField)`
  .MuiInputBase-root {
    padding: 1px;
  }
  .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 21px;
    padding-right: 0;
  }
`;
